import React, { useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../design-system/components/composite/Modal/Modal";
import { CodeBlock } from "../CodeBlock";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import {
  EmbedBodyPad,
  EmbedTitle,
  EmbedStatusText,
  EmbedErrorText,
  EmbedNote,
  FooterRow,
  CancelBtn,
  CopyBtn,
  CopyReason,
} from "./EmbedCodeModal.styles";

export interface EmbedCodeModalProps {
  open: boolean;
  embedCode: string;
  onClose: () => void;
  /** Modal heading. */
  title?: string;
  /** True while the code is still being produced; shows a status line and disables Copy. */
  isLoading?: boolean;
  /** Why the code could not be produced; shown in place of the code. */
  errorMessage?: string | null;
  /** A line under the code, e.g. when the link expires or a limit to know about. */
  note?: string | null;
  /** Status line shown while loading. */
  loadingLabel?: string;
  /** Replaces the built-in code block (e.g. the embed settings panel, which shows the code itself). Footer Copy still copies `embedCode`. */
  children?: React.ReactNode;
  /** Modal width. */
  size?: "md" | "lg" | "xl";
  /** Why Copy is off, shown beside it while there is no code to copy (e.g. the link is turned off). */
  copyDisabledReason?: string | null;
}

export const EmbedCodeModal: React.FC<EmbedCodeModalProps> = ({
  open,
  embedCode,
  onClose,
  title = "Copy embed code",
  isLoading = false,
  errorMessage = null,
  note = null,
  loadingLabel = "Generating…",
  children,
  size = "md",
  copyDisabledReason = null,
}) => {
  const { copiedKey, copy } = useCopyToClipboard();
  const handleCopy = useCallback(() => {
    void copy(embedCode);
  }, [copy, embedCode]);
  const canCopy = !isLoading && !errorMessage && embedCode.length > 0;
  const reason = !canCopy && !isLoading && !errorMessage ? copyDisabledReason : null;

  return (
    <Modal open={open} onClose={onClose} size={size}>
      <ModalHeader onClose={onClose} padding="12px 16px" minHeight="48px">
        <EmbedTitle>{title}</EmbedTitle>
      </ModalHeader>

      <ModalBody padding="0">
        <EmbedBodyPad>
          {isLoading ? (
            <EmbedStatusText role="status">{loadingLabel}</EmbedStatusText>
          ) : errorMessage ? (
            <EmbedErrorText role="alert">{errorMessage}</EmbedErrorText>
          ) : children ?? <CodeBlock code={embedCode} ariaLabel="Embed code" />}
          {note ? <EmbedNote>{note}</EmbedNote> : null}
        </EmbedBodyPad>
      </ModalBody>

      <ModalFooter padding="0" gap="0">
        <FooterRow>
          <CancelBtn variant="outline" size="md" onClick={onClose}>Cancel</CancelBtn>
          {reason ? <CopyReason id="embed-copy-reason">{reason}</CopyReason> : null}
          <CopyBtn
            variant="primary-dark"
            size="md"
            onClick={handleCopy}
            disabled={!canCopy}
            title={reason ?? undefined}
            aria-describedby={reason ? "embed-copy-reason" : undefined}
          >
            {copiedKey ? "Copied!" : "Copy"}
          </CopyBtn>
        </FooterRow>
      </ModalFooter>
    </Modal>
  );
};

export default EmbedCodeModal;
